import { useState } from 'react';
import { useStartEventScan } from '../../api/event-hooks';
import { useCreditShortfall, useEventScanCreditPrice } from '../../api/hooks';
import { ErrorLine } from '../../components/ui/error-line';
import { Modal } from '../../components/ui/modal';
import { WorkingButton } from '../../components/ui/working-button';
import {
  EVENT_SCAN_CANCEL,
  EVENT_SCAN_STARTING,
  eventScanAction,
  eventScanConfirmLine,
  maxPricedLabel,
} from '../../lib/copy';
import { Button } from '../../components/ui/button';

/**
 * **Scan the attendees for one analysis**, behind its confirmation, which says
 * how many are searched on the web, how many on the record are rated from their
 * profile, and how many already scanned are rated on this analysis from what
 * their scan found. The scan rates only that analysis's factors
 * (docs/notes/2026-09-24-analysis-ratings-spec.md).
 *
 * On an account enrolled in live charging the button says the most the scan can
 * cost and starts it on the press, as every priced button does (credits launch
 * spec, step 3); an account not enrolled confirms as before.
 */
export function ScanButton({
  eventId,
  analysis,
  scannable,
  recordRated,
  rerate,
  input = {},
  reportInputs = {},
  disabled = false,
  variant = 'go',
}: {
  eventId: string;
  analysis: string;
  scannable: number;
  recordRated: number;
  rerate: number;
  /** What the office entered for the analysis's parameterized factors. */
  input?: Record<string, string>;
  /** The fields of the report the scan writes when it completes. */
  reportInputs?: Record<string, string>;
  disabled?: boolean;
  variant?: 'go' | 'act';
}) {
  const [confirming, setConfirming] = useState(false);
  const start = useStartEventScan(eventId, analysis);
  const price = useEventScanCreditPrice(eventId, { scannable, recordRated });
  /* Short of the maximum is short: admission holds the whole maximum. */
  const short = useCreditShortfall(price.units);
  const total = scannable + recordRated + rerate;
  if (price.unitsPerCredit !== undefined) {
    return (
      <>
        <WorkingButton
          label={maxPricedLabel(eventScanAction(total), price.units, price.unitsPerCredit)}
          workingLabel={EVENT_SCAN_STARTING}
          working={start.isPending}
          disabled={disabled || price.pending || short !== null}
          variant={variant}
          onClick={() => start.mutate({ input, reportInputs, expectedCreditUnits: price.units })}
        />
        {short ? <span className="credit-short">{short}</span> : null}
        {start.isError ? <ErrorLine error={start.error} /> : null}
      </>
    );
  }
  return (
    <>
      <button
        type="button"
        className={`btn ${variant}`}
        disabled={disabled}
        onClick={() => setConfirming(true)}
      >
        {eventScanAction(total)}
      </button>
      {confirming ? (
        <Modal label={eventScanAction(total)} narrow onClose={() => setConfirming(false)}>
          <div className="modal-head">{eventScanAction(total)}</div>
          <div className="modal-line">{eventScanConfirmLine(scannable, recordRated, rerate)}</div>
          {start.isError ? <ErrorLine error={start.error} /> : null}
          <div className="modal-actions">
            <WorkingButton
              label={eventScanAction(total)}
              workingLabel={EVENT_SCAN_STARTING}
              working={start.isPending}
              onClick={() =>
                start.mutate({ input, reportInputs }, { onSuccess: () => setConfirming(false) })
              }
            />
            <Button variant="act" onClick={() => setConfirming(false)}>
              {EVENT_SCAN_CANCEL}
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
